import React, { useCallback } from "react";
import styles from "./EmailViewer.module.scss";
import fileIcon from "../fileIcon";

const EMAIL_BODY_RESET_STYLES = `<style>
html, body {
  margin: 0 !important;
  padding: 0 !important;
  min-height: 0 !important;
  height: auto !important;
}
</style>`;

const prepareHtmlBody = (html = "") => {
  if (!html) return html;

  if (/<\/head>/i.test(html)) {
    return html.replace(/<\/head>/i, `${EMAIL_BODY_RESET_STYLES}</head>`);
  }

  if (/<html[\s>]/i.test(html)) {
    return html.replace(
      /<html([^>]*)>/i,
      `<html$1><head>${EMAIL_BODY_RESET_STYLES}</head>`
    );
  }

  return `<!DOCTYPE html><html><head>${EMAIL_BODY_RESET_STYLES}</head><body>${html}</body></html>`;
};

const EmailViewer = ({ emailInfo = {} }) => {
    const {
        subject = {},
        fromRecipient,
        toRecipients = [],
        ccRecipients = [],
        bccRecipients = [],
        date,
        attachments = [],
        isHtmlBody,
        body,
        htmlBody,
    } = emailInfo;



    const formatDate = (dateString) => {
        const date = new Date(dateString);

        if (date.getSeconds() > 0) {
            date.setMinutes(date.getMinutes() + 1);
        }
        date.setSeconds(0);

        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    };

    const getInitial = (name) => {
        return name ? name.charAt(0).toUpperCase() : "?";
    };

    const handleIframeLoad = useCallback((event) => {
        const iframe = event.currentTarget;
        const doc = iframe.contentDocument;
        if (!doc) return;

        const height = Math.max(
            doc.documentElement?.scrollHeight || 0,
            doc.body?.scrollHeight || 0
        );

        if (height > 0) {
            iframe.style.height = `${height}px`;
        }
    }, []);

    return (
        <div className={`card shadow-sm h-100 w-100 d-flex flex-column ${styles.emailViewerCard}`}>
            <div className={`fs-md card-header ${styles.emailViewerHeader} ${styles.stickyHeader}`}>
                <strong>{subject}</strong>
            </div>

            <div className={`fs-sm card-header ${styles.emailViewerHeader} ${styles.stickyHeader}`}>
                <div className={styles.avatar}>{getInitial(fromRecipient?.name)}</div>

                <div className={`flex-grow-1 ${styles.emailViewerMeta}`}>
                    <div className="mb-2">
                        {fromRecipient?.name} &lt;{fromRecipient?.emailAddress}&gt;
                    </div>

                    <div className="">
                        To:{" "}
                        {toRecipients?.map((recipient, index) => (
                            <span key={index}>
                                {recipient.name} &lt;{recipient.emailAddress}&gt;
                                {index < toRecipients.length - 1 && ", "}
                            </span>
                        ))}
                    </div>

                    {ccRecipients?.length > 0 && (
                        <div className="">
                            CC:{" "}
                            {ccRecipients?.map((recipient, index) => (
                                <span key={index}>
                                    {recipient.name} &lt;{recipient.emailAddress}&gt;
                                    {index < ccRecipients.length - 1 && ", "}
                                </span>
                            ))}
                        </div>
                    )}

                    {bccRecipients?.length > 0 && (
                        <div className="">
                            BCC:{" "}
                            {bccRecipients?.map((recipient, index) => (
                                <span key={index}>
                                    {recipient.name} &lt;{recipient.emailAddress}&gt;
                                    {index < bccRecipients.length - 1 && ", "}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                <div className={`ml-auto text-muted ${styles.emailViewerDate}`}>
                    {formatDate(date)}
                </div>
            </div>

            {!!attachments.length && (
                <div className={`px-3 mt-3 ${styles.stickyAttachments}`}>
                    <div className={`ps-3 ${styles.emailViewerAttachments}`}>
                        {attachments?.map((attachment, index) => (
                            <div className={`d-flex align-items-center ${styles.attachmentItem}`} key={index}>
                                {fileIcon(attachment?.name)}
                                <span className="ps-1 text-truncate">{attachment.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className={`flex-grow-1 ${styles.emailViewerBody}`}>
                {isHtmlBody ? (
                    <iframe
                        title="Email Content"
                        sandbox="allow-same-origin allow-popups"
                        srcDoc={prepareHtmlBody(htmlBody)}
                        onLoad={handleIframeLoad}
                        className={`w-100 border-0 ${styles.emailBodyFrame}`}
                    />
                ) : (
                    <p className="mb-0 p-3">{body}</p>
                )}
            </div>
        </div>
    );
};

export default EmailViewer;
